import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import "moment/locale/nl";
import Chart from "react-apexcharts";
import { fetchElectricityData, fetchKampeerplaatsen, saveReport, fetchReports } from "../Reducers/actions";

moment.locale('nl');

const ElektriciteitsAnalyse = () => {
  const dispatch = useDispatch();

  // Redux state
  const {
    electricityData,
    electricityDataLoading,
    kampeerplaatsen = [],
    kampeerplaatsenLoading,
    reports = [],
    reportsLoading,
    reportsError
  } = useSelector((state) => state.gastenverbruik);

  const [dateRange, setDateRange] = useState({
    startDate: moment().subtract(30, 'days').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD')
  });

  const [selectedKampeerplaatsen, setSelectedKampeerplaatsen] = useState([]);
  const [showKampeerplaatsDropdown, setShowKampeerplaatsDropdown] = useState(false);
  const kampeerplaatsDropdownRef = useRef(null);

  // Helper functions for date state management
  const setStartDate = (date) => {
    setDateRange(prev => ({ ...prev, startDate: date }));
  };

  const setEndDate = (date) => {
    setDateRange(prev => ({ ...prev, endDate: date }));
  };

  // Local state for analysis data (can be overridden from reports)
  const [analysisData, setAnalysisData] = useState(electricityData || {
    totalKwh: 0,
    monthlyConsumption: 0,
    dailyAverage: 0,
    activePower: { current: 0, peak: 0 },
    reactivePower: 0,
    powerFactor: 0,
    voltage: { current: 0, min: 0, max: 0 },
    current: { current: 0, peak: 0 },
    frequency: 0,
    totalCost: 0,
    costPerKwh: 0,
    relayStatus: 'closed',
    uptime: 100,
    top10Peaks: [],
    hourlyPeaks: [],
    trends: []
  });

  const loadElectricityData = () => {
    const filters = {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate
    };

    // Add selected kampeerplaatsen if any are selected
    if (selectedKampeerplaatsen.length > 0 && selectedKampeerplaatsen.length < (kampeerplaatsen || []).length) {
      filters.kampeerplaatsenIds = selectedKampeerplaatsen;
    }

    console.log('🔄 Loading electricity data with filters:', filters);
    console.log('📍 Selected kampeerplaatsen:', selectedKampeerplaatsen);
    console.log('📍 Available kampeerplaatsen:', kampeerplaatsen);

    dispatch(fetchElectricityData(filters));
  };

  const loadKampeerplaatsen = () => {
    dispatch(fetchKampeerplaatsen());
  };

  const generateReport = () => {
    // Create report data
    const reportData = {
      title: 'Elektriciteitsanalyse Rapport',
      dateRange: {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      },
      selectedKampeerplaatsen: selectedKampeerplaatsen.length > 0
        ? (kampeerplaatsen || []).filter(k => selectedKampeerplaatsen.includes(k._id)).map(k => k.naam)
        : ['Alle kampeerplaatsen'],
      analysisData: analysisData,
      generatedAt: new Date().toISOString(),
      generatedBy: 'System' // Could be user info from Redux state
    };

    // Save report to backend
    dispatch(saveReport(reportData));
  };

  useEffect(() => {
    loadKampeerplaatsen();
  }, [dispatch]);

  useEffect(() => {
    if (kampeerplaatsen.length > 0) {
      loadElectricityData();
    }
  }, [kampeerplaatsen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (kampeerplaatsDropdownRef.current && !kampeerplaatsDropdownRef.current.contains(event.target)) {
        setShowKampeerplaatsDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Sync electricityData from Redux to local state
  useEffect(() => {
    if (electricityData) {
      setAnalysisData(electricityData);
    }
  }, [electricityData]);

  // Load reports on component mount
  useEffect(() => {
    dispatch(fetchReports());
  }, [dispatch]);

  useEffect(() => {
    if (kampeerplaatsen.length > 0) {
      loadElectricityData();
    }
  }, [selectedKampeerplaatsen]);

  const handleDateRangeChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const handleKampeerplaatsToggle = (kampeerplaats) => {
    setSelectedKampeerplaatsen(prev => {
      if (prev.includes(kampeerplaats)) {
        return prev.filter(k => k !== kampeerplaats);
      } else {
        return [...prev, kampeerplaats];
      }
    });
  };

  const handleSelectAllKampeerplaatsen = () => {
    if (selectedKampeerplaatsen.length === (kampeerplaatsen || []).length) {
      setSelectedKampeerplaatsen([]);
    } else {
      setSelectedKampeerplaatsen((kampeerplaatsen || []).map(k => k._id));
    }
  };

  const getSelectedKampeerplaatsenText = () => {
    if (selectedKampeerplaatsen.length === 0) {
      return 'Alle kampeerplaatsen';
    } else if (selectedKampeerplaatsen.length === 1) {
      const selected = (kampeerplaatsen || []).find(k => k._id === selectedKampeerplaatsen[0]);
      return selected ? selected.naam : 'Onbekende kampeerplaats';
    } else if (selectedKampeerplaatsen.length === (kampeerplaatsen || []).length) {
      return 'Alle kampeerplaatsen';
    } else {
      return `${selectedKampeerplaatsen.length} kampeerplaatsen geselecteerd`;
    }
  };

  const getHourlyPeaksChart = () => {
    const hourlyPeaks = analysisData.hourlyPeaks || [];

    // Generate default 24-hour data if no data available
    const defaultHourlyData = Array.from({ length: 24 }, (_, hour) => ({
      hour: hour.toString().padStart(2, '0') + ':00',
      maxPower: '0.00',
      avgPower: '0.00'
    }));

    const chartData = hourlyPeaks.length > 0 ? hourlyPeaks : defaultHourlyData;

    return {
      chart: {
        type: 'line',
        height: 350,
        toolbar: { show: false }
      },
      series: [
        {
          name: 'Max Vermogen',
          data: chartData.map(item => parseFloat(item.maxPower || 0))
        },
        {
          name: 'Gem. Vermogen',
          data: chartData.map(item => parseFloat(item.avgPower || 0))
        }
      ],
      xaxis: {
        categories: chartData.map(item => item.hour),
        title: { text: 'Uur van de dag' }
      },
      yaxis: {
        title: { text: 'Vermogen (kW)' }
      },
      colors: ['#ff4757', '#2ed573'],
      stroke: { width: 3 },
      markers: { size: 4 }
    };
  };

  const getTrendsChart = () => {
    const trends = analysisData.trends || [];

    // Generate default 7-day data if no data available
    const defaultTrendsData = Array.from({ length: 7 }, (_, index) => {
      const date = moment().subtract(6 - index, 'days');
      return {
        date: date.format('DD-MM'),
        consumption: '0.00',
        cost: '0.00'
      };
    });

    const chartData = trends.length > 0 ? trends : defaultTrendsData;

    return {
      chart: {
        type: 'line',
        height: 350,
        toolbar: { show: false }
      },
      series: [
        {
          name: 'Verbruik (kWh)',
          type: 'column',
          data: chartData.map(item => parseFloat(item.consumption || 0))
        },
        {
          name: 'Kosten (€)',
          type: 'line',
          data: chartData.map(item => parseFloat(item.cost || 0))
        }
      ],
      xaxis: {
        categories: chartData.map(item => item.date),
        title: { text: 'Datum' }
      },
      yaxis: [
        {
          title: { text: 'Verbruik (kWh)' }
        },
        {
          opposite: true,
          title: { text: 'Kosten (€)' }
        }
      ],
      colors: ['#3742fa', '#ff6b81']
    };
  };

  if (electricityDataLoading || kampeerplaatsenLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="content-header">
            <h1 className="content-header-title">
              <i className="fas fa-bolt mr-3"></i>
              Elektriciteitsanalyse - IVY Metering EM114039-02
            </h1>
            <div className="content-header-subtitle">
              Uitgebreide analyse van stroomverbruik, pieken en operationele parameters
            </div>
          </div>
        </div>
      </div>

      {/* ChirpStack Tags Display */}
      {analysisData.kampeerplaatsen && analysisData.kampeerplaatsen.some(k => k.device && Object.keys(k.device.tags).length > 0) && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="card">
              <div className="card-header py-3">
                <h6 className="m-0 font-weight-bold text-primary">
                  <i className="fas fa-tags mr-2"></i>
                  ChirpStack Device Tags
                </h6>
              </div>
              <div className="card-body">
                <div className="row">
                  {analysisData.kampeerplaatsen
                    .filter(k => k.device && Object.keys(k.device.tags).length > 0)
                    .map((kampeerplaats) => (
                      <div key={kampeerplaats._id} className="col-md-6 col-lg-4 mb-3">
                        <div className="card border-left-info">
                          <div className="card-body">
                            <h6 className="font-weight-bold text-info mb-2">
                              {kampeerplaats.naam}
                            </h6>
                            <p className="text-sm text-muted mb-2">
                              Device: {kampeerplaats.device.naam}
                            </p>
                            <div className="tags-container">
                              {Object.entries(kampeerplaats.device.tags).map(([key, value]) => (
                                <div key={key} className="mb-1">
                                  <span className="badge badge-info mr-1">{key}:</span>
                                  <small className="text-muted">{value}</small>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Date Range Filter */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="row align-items-center">
                <div className="col-md-3">
                  <label className="form-label">Van datum:</label>
                  <input
                    type="date"
                    className="form-control"
                    value={dateRange.startDate}
                    onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Tot datum:</label>
                  <input
                    type="date"
                    className="form-control"
                    value={dateRange.endDate}
                    onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Kampeerplaatsen:</label>
                  <div className="dropdown" ref={kampeerplaatsDropdownRef}>
                    <button
                      className="btn btn-outline-secondary dropdown-toggle w-100 text-left"
                      type="button"
                      onClick={() => setShowKampeerplaatsDropdown(!showKampeerplaatsDropdown)}
                      style={{ textAlign: 'left' }}
                    >
                      <i className="fas fa-campground mr-2"></i>
                      {getSelectedKampeerplaatsenText()}
                    </button>
                    {showKampeerplaatsDropdown && (
                      <div className="dropdown-menu show w-100" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        <div className="dropdown-item">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={selectedKampeerplaatsen.length === kampeerplaatsen.length}
                              onChange={handleSelectAllKampeerplaatsen}
                            />
                            <label className="form-check-label font-weight-bold">
                              Alle kampeerplaatsen
                            </label>
                          </div>
                        </div>
                        <div className="dropdown-divider"></div>
                        {(kampeerplaatsen || []).map((kampeerplaats) => (
                          <div key={kampeerplaats._id} className="dropdown-item">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={selectedKampeerplaatsen.includes(kampeerplaats._id)}
                                onChange={() => handleKampeerplaatsToggle(kampeerplaats._id)}
                              />
                              <label className="form-check-label">
                                {kampeerplaats.naam}
                              </label>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-md-3 d-flex align-items-end">
                  <button
                    className="btn btn-primary mr-2"
                    onClick={loadElectricityData}
                    disabled={electricityDataLoading}
                  >
                    <i className="fas fa-sync-alt mr-2"></i>
                    Vernieuwen
                  </button>
                  <button
                    className="btn btn-success"
                    onClick={generateReport}
                    disabled={electricityDataLoading || !analysisData.totalKwh}
                  >
                    <i className="fas fa-file-pdf mr-2"></i>
                    Genereer Rapport
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="row mb-4">
        <div className="col-xl-2 col-md-4 mb-4">
          <div className="card border-left-primary shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">
                    Totaal Verbruik
                  </div>
                  <div className="h6 mb-0 font-weight-bold text-gray-800">
                    {Number(analysisData.totalKwh || 0).toFixed(1)} kWh
                  </div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-chart-area fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-2 col-md-4 mb-4">
          <div className="card border-left-success shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-success text-uppercase mb-1">
                    Huidig Vermogen
                  </div>
                  <div className="h6 mb-0 font-weight-bold text-gray-800">
                    {Number(analysisData.activePower?.current || 0)} kW
                  </div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-bolt fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-2 col-md-4 mb-4">
          <div className="card border-left-warning shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-warning text-uppercase mb-1">
                    Piek Vermogen
                  </div>
                  <div className="h6 mb-0 font-weight-bold text-gray-800">
                    {Number(analysisData.activePower?.peak || 0)} kW
                  </div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-chart-line fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-2 col-md-4 mb-4">
          <div className="card border-left-info shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-info text-uppercase mb-1">
                    Spanning
                  </div>
                  <div className="h6 mb-0 font-weight-bold text-gray-800">
                    {Number(analysisData.voltage?.current || 0)} V
                  </div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-plug fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-2 col-md-4 mb-4">
          <div className="card border-left-secondary shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-secondary text-uppercase mb-1">
                    Power Factor
                  </div>
                  <div className="h6 mb-0 font-weight-bold text-gray-800">
                    {Number(analysisData.powerFactor || 0).toFixed(2)}
                  </div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-wave-square fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-2 col-md-4 mb-4">
          <div className="card border-left-dark shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-dark text-uppercase mb-1">
                    Totale Kosten
                  </div>
                  <div className="h6 mb-0 font-weight-bold text-gray-800">
                    {formatCurrency(analysisData.totalCost)}
                  </div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-euro-sign fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="row mb-4">
        <div className="col-xl-6 col-lg-6">
          <div className="card shadow mb-4">
            <div className="card-header py-3">
              <h6 className="m-0 font-weight-bold text-primary">Piekvermogens per Uur (24-uurs overzicht)</h6>
            </div>
            <div className="card-body">
              <Chart
                options={getHourlyPeaksChart()}
                series={getHourlyPeaksChart().series}
                type="line"
                height={350}
              />
            </div>
          </div>
        </div>

        <div className="col-xl-6 col-lg-6">
          <div className="card shadow mb-4">
            <div className="card-header py-3">
              <h6 className="m-0 font-weight-bold text-primary">Dagelijkse Trends - Verbruik & Kosten</h6>
            </div>
            <div className="card-body">
              <Chart
                options={getTrendsChart()}
                series={getTrendsChart().series}
                type="line"
                height={350}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Top 10 Peaks Table */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow mb-4">
            <div className="card-header py-3">
              <h6 className="m-0 font-weight-bold text-primary">🔥 TOP 10 PIEKMOMENTEN</h6>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>Rang</th>
                      <th>Datum/Tijd</th>
                      <th>Vermogen (kW)</th>
                      <th>Stroom (A)</th>
                      <th>Duur (min)</th>
                      <th>% boven gemiddelde</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(analysisData.top10Peaks || []).length > 0 ? (
                      analysisData.top10Peaks.map((peak) => (
                        <tr key={peak.rank}>
                          <td>
                            <span className={`badge ${peak.rank <= 3 ? 'badge-danger' : peak.rank <= 6 ? 'badge-warning' : 'badge-info'}`}>
                              {peak.rank}
                            </span>
                          </td>
                          <td>{peak.datetime}</td>
                          <td><strong>{peak.power} kW</strong></td>
                          <td>{peak.current} A</td>
                          <td>{peak.duration}</td>
                          <td>
                            <span className={`badge ${peak.percentageAboveAverage > 100 ? 'badge-danger' : 'badge-warning'}`}>
                              +{peak.percentageAboveAverage}%
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center text-muted">
                          <i className="fas fa-chart-bar fa-2x mb-2 d-block"></i>
                          Geen piekgegevens beschikbaar. Selecteer een datumbereik en kampeerplaatsen en klik op "Vernieuwen" om data te laden.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Parameters */}
      <div className="row mb-4">
        <div className="col-xl-4 col-lg-4">
          <div className="card shadow mb-4">
            <div className="card-header py-3">
              <h6 className="m-0 font-weight-bold text-primary">Elektrische Parameters</h6>
            </div>
            <div className="card-body">
              <div className="row mb-3">
                <div className="col-6"><strong>Spanning:</strong></div>
                <div className="col-6">{analysisData.voltage.current} V</div>
              </div>
              <div className="row mb-3">
                <div className="col-6">Min/Max:</div>
                <div className="col-6">{analysisData.voltage.min} - {analysisData.voltage.max} V</div>
              </div>
              <div className="row mb-3">
                <div className="col-6"><strong>Stroom:</strong></div>
                <div className="col-6">{analysisData.current.current} A</div>
              </div>
              <div className="row mb-3">
                <div className="col-6">Piek stroom:</div>
                <div className="col-6">{analysisData.current.peak} A</div>
              </div>
              <div className="row mb-3">
                <div className="col-6"><strong>Frequentie:</strong></div>
                <div className="col-6">{analysisData.frequency} Hz</div>
              </div>
              <div className="row mb-3">
                <div className="col-6"><strong>Reactief vermogen:</strong></div>
                <div className="col-6">{analysisData.reactivePower} kvar</div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-4 col-lg-4">
          <div className="card shadow mb-4">
            <div className="card-header py-3">
              <h6 className="m-0 font-weight-bold text-primary">Financiële Analyse</h6>
            </div>
            <div className="card-body">
              <div className="row mb-3">
                <div className="col-6"><strong>Maandverbruik:</strong></div>
                <div className="col-6">{analysisData.monthlyConsumption} kWh</div>
              </div>
              <div className="row mb-3">
                <div className="col-6"><strong>Daggemiddelde:</strong></div>
                <div className="col-6">{analysisData.dailyAverage} kWh</div>
              </div>
              <div className="row mb-3">
                <div className="col-6"><strong>Kosten per kWh:</strong></div>
                <div className="col-6">{formatCurrency(analysisData.costPerKwh)}</div>
              </div>
              <div className="row mb-3">
                <div className="col-6"><strong>Piekkosten impact:</strong></div>
                <div className="col-6">
                  <span className="badge badge-warning">+15% capaciteit</span>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-6"><strong>Besparingspotentieel:</strong></div>
                <div className="col-6">
                  <span className="badge badge-success">€45-80/maand</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-4 col-lg-4">
          <div className="card shadow mb-4">
            <div className="card-header py-3">
              <h6 className="m-0 font-weight-bold text-primary">Operationele Status</h6>
            </div>
            <div className="card-body">
              <div className="row mb-3">
                <div className="col-6"><strong>Relais status:</strong></div>
                <div className="col-6">
                  <span className={`badge ${analysisData.relayStatus === 'closed' ? 'badge-success' : 'badge-danger'}`}>
                    {analysisData.relayStatus === 'closed' ? 'Gesloten' : 'Open'}
                  </span>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-6"><strong>Uptime:</strong></div>
                <div className="col-6">
                  <span className="badge badge-success">{analysisData.uptime}%</span>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-6"><strong>Werkmodus:</strong></div>
                <div className="col-6">Post-paid</div>
              </div>
              <div className="row mb-3">
                <div className="col-6"><strong>Laatste storing:</strong></div>
                <div className="col-6">Geen</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recommendations */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow mb-4">
            <div className="card-header py-3">
              <h6 className="m-0 font-weight-bold text-primary">💡 Aanbevelingen & Analyses</h6>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <h6><i className="fas fa-chart-line text-warning"></i> Piekanalyse</h6>
                  <ul>
                    <li>Hoogste pieken tussen 17:00-19:00 (avondspits)</li>
                    <li>Terugkerende pieken tijdens weekdagen</li>
                    <li>Onverwachte piek gedetecteerd op zondag 15:30</li>
                  </ul>

                  <h6 className="mt-4"><i className="fas fa-lightbulb text-success"></i> Besparingsadvies</h6>
                  <ul>
                    <li>Verdeel apparatuur gebruik over de dag</li>
                    <li>Gebruik timers voor niet-kritische apparaten</li>
                    <li>Overweeg energieopslag voor piekafvlakking</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h6><i className="fas fa-balance-scale text-info"></i> Load Balancing</h6>
                  <ul>
                    <li>Huidige aansluiting: voldoende capaciteit</li>
                    <li>Aanbeveling: 3-fase verdeling optimaliseren</li>
                    <li>Monitoring: power factor verbeteren (nu 0.92)</li>
                  </ul>

                  <h6 className="mt-4"><i className="fas fa-exclamation-triangle text-danger"></i> Aandachtspunten</h6>
                  <ul>
                    <li>Spanning variatie binnen normale grenzen</li>
                    <li>Frequentie stabiel op 50.02 Hz</li>
                    <li>Geen kritieke afwijkingen gedetecteerd</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reports History */}
      <div className="row">
        <div className="col-lg-12">
          <div className="card shadow mb-4">
            <div className="card-header py-3 d-flex justify-content-between align-items-center">
              <h6 className="m-0 font-weight-bold text-primary">📊 Opgeslagen Rapporten</h6>
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => dispatch(fetchReports())}
              >
                <i className="fas fa-sync-alt"></i> Vernieuwen
              </button>
            </div>
            <div className="card-body">
              {reportsLoading ? (
                <div className="text-center">
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                </div>
              ) : reportsError ? (
                <div className="alert alert-danger" role="alert">
                  {reportsError}
                </div>
              ) : reports.length === 0 ? (
                <div className="alert alert-info" role="alert">
                  <i className="fas fa-info-circle"></i> Nog geen rapporten opgeslagen. Klik op "Genereer Rapport" om uw eerste rapport aan te maken.
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th>Titel</th>
                        <th>Periode</th>
                        <th>Kampeerplaatsen</th>
                        <th>Totaal Verbruik</th>
                        <th>Kosten</th>
                        <th>Aangemaakt</th>
                        <th>Acties</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(reports || []).map((report) => (
                        <tr key={report.id}>
                          <td>{report.title}</td>
                          <td>
                            {moment(report.startDate).format('DD-MM-YYYY')} - {moment(report.endDate).format('DD-MM-YYYY')}
                          </td>
                          <td>
                            {(report.selectedKampeerplaatsen || []).length === 0
                              ? 'Alle kampeerplaatsen'
                              : `${(report.selectedKampeerplaatsen || []).length} geselecteerd`
                            }
                          </td>
                          <td>{Number((report.analysisData || {}).totalKwh || 0).toFixed(1)} kWh</td>
                          <td>{formatCurrency((report.analysisData || {}).totalCost || 0)}</td>
                          <td>{moment(report.createdAt).format('DD-MM-YYYY HH:mm')}</td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => {
                                // Load the report data into the current view
                                setStartDate(report.startDate || dateRange.startDate);
                                setEndDate(report.endDate || dateRange.endDate);
                                setSelectedKampeerplaatsen(report.selectedKampeerplaatsen || []);
                                setAnalysisData(report.analysisData || analysisData);
                              }}
                              title="Rapport laden"
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ElektriciteitsAnalyse;